import { useTranslation } from "react-i18next";
import { Tooltip } from "../../common";
import {
  getDistributionSegmentTotal,
  getSegmentPercentages,
} from "../../../helpers/usage/usageStatusChartHelpers";
import { UsageDistributionSegment } from "../../../types/analytics/UsageDistributionSegment";
import colourHelper from "../../../helpers/colourHelper";
import UsageStatusLegendExplainer from "./UsageStatusLegendExplainer";

export interface UsageStatusSegmentClick {
  id: string;
  displayLabel: string;
  value: number;
}

interface UsageStatusDistributionBarProps {
  segments: UsageDistributionSegment[];
  onSegmentFilter?: (segment: UsageStatusSegmentClick) => void;
}

/** Below this share the segment is too narrow to hold a readable percentage. */
const MIN_WIDTH_FOR_INLINE_PERCENT = 8;

function UsageStatusDistributionBar({
  segments,
  onSegmentFilter,
}: UsageStatusDistributionBarProps) {
  const { t } = useTranslation();
  const total = getDistributionSegmentTotal(segments);
  const percentages = getSegmentPercentages(segments);

  if (segments.length === 0 || total === 0) {
    return null;
  }

  return (
    <div>
      <div
        className="flex h-7 w-full overflow-hidden rounded-md bg-gray-100"
        role="group"
        aria-label={t(
          "Analytics.Widgets.Usage.Band.DistributionAriaLabel",
          "Journey status distribution",
        )}
      >
        {segments.map((segment) => {
          const widthPercent = (segment.value / total) * 100;
          const percentage = percentages[segment.id] ?? 0;
          const tooltipText = t(
            "Analytics.Widgets.Usage.Band.SegmentTooltip",
            "{{label}}: {{formattedCount}} ({{percent}}%)",
            {
              label: segment.displayLabel,
              formattedCount: segment.value.toLocaleString(),
              percent: percentage,
            },
          );

          const segmentButton = (
            <button
              type="button"
              className="flex h-full w-full min-w-[2px] items-center justify-center text-xs font-semibold transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
              style={{
                backgroundColor: segment.color,
                color: colourHelper.shadeColour(segment.color, -38),
              }}
              aria-label={tooltipText}
              onClick={
                onSegmentFilter
                  ? () =>
                      onSegmentFilter({
                        id: segment.id,
                        displayLabel: segment.displayLabel,
                        value: segment.value,
                      })
                  : undefined
              }
            >
              {widthPercent >= MIN_WIDTH_FOR_INLINE_PERCENT && (
                <span aria-hidden>{percentage}%</span>
              )}
            </button>
          );

          return (
            <div
              key={segment.id}
              className="h-full min-w-[2px]"
              style={{ width: `${widthPercent}%` }}
            >
              <Tooltip content={tooltipText} triggerElement={segmentButton} />
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {segments.map((segment) => (
            <li
              key={`legend-${segment.id}`}
              className="inline-flex items-center gap-1.5 text-xs text-gray-600"
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
                aria-hidden
              />
              <span>{segment.displayLabel}</span>
            </li>
          ))}
        </ul>

        <UsageStatusLegendExplainer segments={segments} />
      </div>
    </div>
  );
}

export default UsageStatusDistributionBar;
